import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import PaymentModal from '../../components/ui/PaymentModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CanvasPreview from './components/CanvasPreview';
import ReportConfiguration from './components/ReportConfiguration';
import AnalysisInsights from './components/AnalysisInsights';
import ExportOptionsModal from './components/ExportOptionsModal';

const BusinessModelCanvasReportGenerator = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('executive-summary');
  const [reportConfig, setReportConfig] = useState({
    branding: {
      companyName: 'Business Jadhak',
      logo: null,
      colorScheme: 'default'
    },
    sections: {
      canvasOverview: true,
      strategicAnalysis: true,
      swotAnalysis: true,
      recommendations: true,
      financialProjections: true,
      competitiveAnalysis: true
    },
    includeCharts: true,
    includeAppendix: true
  });

  // Mock canvas data with completion tracking
  const canvasData = {
    blocks: [
      { id: 'key-partners', title: 'Key Partners', completion: 85, value: 'Technology vendors, Strategic distribution partners, Industry associations' },
      { id: 'key-activities', title: 'Key Activities', completion: 90, value: 'Product development, Quality assurance, Marketing campaigns' },
      { id: 'key-resources', title: 'Key Resources', completion: 80, value: 'Skilled workforce, Proprietary technology, Brand reputation' },
      { id: 'value-proposition', title: 'Value Proposition', completion: 95, value: 'Comprehensive business intelligence platform with AI-powered insights' },
      { id: 'customer-relationships', title: 'Customer Relationships', completion: 75, value: 'Personalized onboarding, Self-service portal, Proactive support' },
      { id: 'channels', title: 'Channels', completion: 88, value: 'Direct sales, Digital marketing, Partner network' },
      { id: 'customer-segments', title: 'Customer Segments', completion: 92, value: 'Early-stage startups, SMEs, Business consultants' },
      { id: 'cost-structure', title: 'Cost Structure', completion: 78, value: 'Technology infrastructure, Personnel expenses, Marketing costs' },
      { id: 'revenue-streams', title: 'Revenue Streams', completion: 85, value: 'Subscription-based pricing, Premium features, Professional services' }
    ],
    overallCompletion: 85,
    lastUpdated: '2026-02-08'
  };

  const reportTemplates = [
    {
      id: 'executive-summary',
      name: 'Executive Summary',
      description: 'Concise overview for stakeholders and investors',
      icon: 'FileText',
      pages: '8-12 pages',
      color: 'primary'
    },
    {
      id: 'detailed-analysis',
      name: 'Detailed Analysis',
      description: 'Comprehensive strategic analysis with deep insights',
      icon: 'BookOpen',
      pages: '20-30 pages',
      color: 'success'
    },
    {
      id: 'investor-presentation',
      name: 'Investor Presentation',
      description: 'Pitch-ready format with financial projections',
      icon: 'TrendingUp',
      pages: '15-20 pages',
      color: 'warning'
    },
    {
      id: 'strategic-review',
      name: 'Strategic Review',
      description: 'Internal strategy document for team alignment',
      icon: 'Target',
      pages: '12-18 pages',
      color: 'accent'
    }
  ];

  const handleGenerateReport = () => {
    if (canvasData?.overallCompletion < 70) {
      alert('Canvas completion must be at least 70% to generate reports.');
      return;
    }

    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setIsExportModalOpen(true);
    }, 2000);
  };

  const handleExport = (format) => {
    setIsPaymentModalOpen(true);
    setIsExportModalOpen(false);
  };

  const handlePaymentSuccess = () => {
    alert(`Report exported successfully as ${selectedTemplate}!`);
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthHeader isAuthenticated userName="Business Owner" userSubscription="free" />
      <Sidebar isCollapsed={isSidebarCollapsed} userSubscription="free" />
      <main className={`pt-20 transition-smooth ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => navigate('/business-model-canvas')}
                    className="p-2 hover:bg-muted rounded-md transition-smooth"
                    aria-label="Back to canvas"
                  >
                    <Icon name="ArrowLeft" size={20} color="var(--color-muted-foreground)" />
                  </button>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                    Generate Reports
                  </h1>
                </div>
                <p className="text-sm md:text-base text-muted-foreground font-body ml-12">
                  Create comprehensive reports from your Business Model Canvas
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                onClick={handleGenerateReport}
                loading={isGenerating}
                disabled={canvasData?.overallCompletion < 70}
                iconName="FileText"
                iconPosition="left"
              >
                Generate Report
              </Button>
            </div>

            {/* Canvas Health Score */}
            <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    canvasData?.overallCompletion >= 80 ? 'bg-success/20' : 'bg-warning/20'
                  }`}>
                    <Icon
                      name={canvasData?.overallCompletion >= 80 ? 'CheckCircle2' : 'AlertCircle'}
                      size={24}
                      color={canvasData?.overallCompletion >= 80 ? 'var(--color-success)' : 'var(--color-warning)'}
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-foreground">
                      Canvas Health Score
                    </h3>
                    <p className="text-sm text-muted-foreground font-caption">
                      Last updated: {canvasData?.lastUpdated}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-heading font-bold ${
                    canvasData?.overallCompletion >= 80 ? 'text-success' : 'text-warning'
                  }`}>
                    {canvasData?.overallCompletion}%
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">
                    {canvasData?.blocks?.filter(b => b?.completion >= 70)?.length} / {canvasData?.blocks?.length} blocks complete
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Report Templates */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
              Select Report Template
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {reportTemplates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => setSelectedTemplate(template?.id)}
                  className={`p-4 border-2 rounded-lg transition-smooth text-left ${
                    selectedTemplate === template?.id
                      ? 'border-primary bg-primary/10 elevation-md' :'border-border hover:border-primary/50 hover:elevation-sm'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg mb-3 flex items-center justify-center ${
                    selectedTemplate === template?.id ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    <Icon
                      name={template?.icon}
                      size={24}
                      color={selectedTemplate === template?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground mb-1">
                    {template?.name}
                  </h3>
                  <p className="text-xs text-muted-foreground font-caption mb-2">
                    {template?.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={12} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground font-caption">
                      {template?.pages}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Canvas Preview & Configuration */}
            <div className="lg:col-span-2 space-y-6">
              <CanvasPreview canvasData={canvasData} />
              <ReportConfiguration
                config={reportConfig}
                onConfigChange={setReportConfig}
                selectedTemplate={selectedTemplate}
              />
            </div>

            {/* Right Column - Analysis Insights */}
            <div className="lg:col-span-1">
              <AnalysisInsights canvasData={canvasData} />
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {isExportModalOpen && (
        <ExportOptionsModal
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
          selectedTemplate={selectedTemplate}
          reportConfig={reportConfig}
        />
      )}

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        featureName="Report Export"
        featureDescription="Export comprehensive Business Model Canvas reports in multiple formats"
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default BusinessModelCanvasReportGenerator;